// One non-PHI return hint for a kiosk-launched V2 package. The package token,
// short code, answers, patient name, phone and DOB are never stored here.

const STORAGE_KEY = 'operaitor_kiosk_return:v1';
const KIOSK_PATH = /^\/kiosk\/[A-Za-z0-9-]{1,100}$/;

export interface KioskReturnRecord {
  v: 1;
  package_instance_id: string;
  return_path: string;
  expires_at: number;
}

function validRecord(value: unknown): value is KioskReturnRecord {
  if (!value || typeof value !== 'object') return false;
  const record = value as Partial<KioskReturnRecord>;
  return record.v === 1
    && typeof record.package_instance_id === 'string'
    && record.package_instance_id.length > 0
    && typeof record.return_path === 'string'
    && KIOSK_PATH.test(record.return_path)
    && typeof record.expires_at === 'number'
    && Number.isFinite(record.expires_at)
    && record.expires_at > Date.now();
}

export function saveKioskReturn(
  packageInstanceId: string,
  returnPath: string,
  expiresAt: string
): void {
  const parsedExpiry = Date.parse(expiresAt);
  if (!packageInstanceId || !KIOSK_PATH.test(returnPath) || !Number.isFinite(parsedExpiry)) return;
  const record: KioskReturnRecord = {
    v: 1,
    package_instance_id: packageInstanceId,
    return_path: returnPath,
    expires_at: parsedExpiry,
  };
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch { /* kiosk still works; it just cannot offer an automatic return */ }
}

export function readKioskReturn(
  packageInstanceId?: string
): KioskReturnRecord | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!validRecord(parsed)) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (packageInstanceId && parsed.package_instance_id !== packageInstanceId) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearKioskReturn(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* nothing to clear */ }
}
